import { computed, onBeforeUnmount, ref, watch } from 'vue';

/**
 * Ochrona przed cichą utratą zmian na kartach szczegółów.
 *
 * Karty składają się z sekcji, z których każda zapisuje się osobno — na karcie
 * leada są cztery takie sekcje, na karcie oddziału sześć. Do 2026-08 tylko
 * jedna z dwunastu mówiła użytkownikowi, że coś jest niezapisane, a wyjście
 * z widoku kasowało wpisane dane bez słowa (analiza:
 * `docs/analiza-zapis-na-kartach-szczegolow.md`).
 *
 * Sekcja zgłasza tu swój stan, a rejestr zbiorczy pozwala strażnikowi trasy
 * zapytać jednym pytaniem, zamiast dopytywać sekcja po sekcji.
 */

// Klucze sekcji, które mają niezapisane zmiany. `ref` na zbiorze, nie zbiór
// samą w sobie: `computed` w strażniku musi widzieć zmianę liczebności.
const zrodla = ref(new Set());

// Sekcje, które oddały swoją funkcję zapisu — pasek karty potrzebuje ich, żeby
// zapisać wszystko jednym kliknięciem. Klucz ten sam co w `zrodla`.
const zapisywalne = new Map();

let licznik = 0;

/** Czy cokolwiek na ekranie czeka na zapis. */
export function saNiezapisaneZmiany() {
    return zrodla.value.size > 0;
}

/** Nazwy sekcji z niezapisanymi zmianami — do komunikatu dla użytkownika. */
export function nazwyNiezapisanych() {
    return [...zrodla.value].map(w => w.split('|')[1]).filter(Boolean);
}

/**
 * Zapisuje wszystkie sekcje z niezapisanymi zmianami.
 *
 * Sekcje odkładają się do RÓŻNYCH punktów API, więc żądania idą równolegle
 * i częściowe powodzenie jest dozwolone (decyzja właściciela 2026-08-24):
 * to, co przeszło, zostaje zapisane, a wołający dostaje nazwy tych sekcji,
 * które trzeba ponowić. Wariant „wszystko albo nic" wymagałby transakcji
 * przez kilka punktów naraz, czego backend nie ma.
 *
 * @returns {Promise<{udane: string[], nieudane: string[]}>}
 */
export async function zapiszNiezapisaneZmiany() {
    const doZapisu = [...zrodla.value]
        .map(klucz => ({ klucz, wpis: zapisywalne.get(klucz) }))
        .filter(({ wpis }) => wpis);

    const wyniki = await Promise.allSettled(doZapisu.map(({ wpis }) => wpis.zapisz()));

    const udane = [];
    const nieudane = [];

    wyniki.forEach((wynik, i) => {
        const nazwa = doZapisu[i].wpis.nazwa;
        (wynik.status === 'fulfilled' ? udane : nieudane).push(nazwa);
    });

    return { udane, nieudane };
}

/**
 * Porzucenie zmian: po świadomej decyzji użytkownika rejestr ma być pusty,
 * inaczej strażnik zapyta drugi raz przy następnym przejściu.
 */
export function zapomnijNiezapisaneZmiany() {
    zrodla.value = new Set();
}

/** Czy którakolwiek z czekających sekcji umie zapisać się sama. */
export function saSekcjeDoZapisu() {
    return [...zrodla.value].some(klucz => zapisywalne.has(klucz));
}

/** Odcisk stanu — porównujemy wartości, nie referencje. */
function odcisk(stan) {
    return JSON.stringify(stan ?? null);
}

/**
 * @param {() => unknown} pobierzStan  migawka pól sekcji (obiekt albo tablica)
 * @param {string} nazwaSekcji         nazwa widoczna w pytaniu przed wyjściem
 */
export function useNiezapisaneZmiany(pobierzStan, nazwaSekcji = '', opcje = {}) {
    const klucz = `${++licznik}|${nazwaSekcji}`;

    // Sekcja oddaje swój zapis paskowi karty. Bez tego zostaje przy własnym
    // przycisku — tak działają sekcje, których „zapis" jest w istocie akcją
    // (zmiana etapu, wysyłka pliku).
    if (typeof opcje.zapisz === 'function') {
        zapisywalne.set(klucz, { nazwa: nazwaSekcji, zapisz: opcje.zapisz });
    }
    const zapisany = ref(odcisk(pobierzStan()));

    const niezapisane = computed(() => odcisk(pobierzStan()) !== zapisany.value);

    watch(niezapisane, (jest) => {
        const kopia = new Set(zrodla.value);
        jest ? kopia.add(klucz) : kopia.delete(klucz);
        zrodla.value = kopia;
    }, { immediate: true });

    // Sekcja znika z ekranu — znika też z rejestru, inaczej strażnik pytałby
    // o dane, których nie ma już gdzie zapisać.
    onBeforeUnmount(() => {
        const kopia = new Set(zrodla.value);
        kopia.delete(klucz);
        zrodla.value = kopia;
        zapisywalne.delete(klucz);
    });

    /** Po udanym zapisie: bieżący stan staje się punktem odniesienia. */
    function zapamietaj() {
        zapisany.value = odcisk(pobierzStan());
    }

    return { niezapisane, zapamietaj };
}

// Zamknięcie karty i odświeżenie strony omijają strażnika trasy — tu może
// zapytać wyłącznie przeglądarka, własnym oknem. Nasłuch zakładamy raz,
// przy wczytaniu modułu; sam sprawdza rejestr, więc nic nie kosztuje, gdy
// nie ma czego bronić.
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (zdarzenie) => {
        if (! saNiezapisaneZmiany()) {
            return;
        }

        zdarzenie.preventDefault();
        zdarzenie.returnValue = '';
    });
}
